import { useState } from "react";

/* --- Data ----------------------------------------------------------------- */
// Each entry is one of:
//   { name, tag, slug }     → fetched from simpleicons.org in the brand's official color
//   { name, tag, domain }   → fetched as a Google favicon at 128px (fallback for niche brands
//                             that aren't in simpleicons, e.g. CyberSource, NMI, IXOPAY)
//   { name, tag, logoDev }  → fetched from logo.dev (full wordmark, replaces deprecated Clearbit)
//   { name, tag, asset }    → bundled with the app (used when no clean CDN logo is available)

const LOGO_DEV_TOKEN = "pk_X-1ZO13GSgeOoUrIuJ6GMQ";

const COL_A = [
  { name: "Visa", tag: "Card scheme", slug: "visa" },
  { name: "Stripe", tag: "Processor", domain: "stripe.com" },
  { name: "Adyen", tag: "Acquirer", slug: "adyen" },
  { name: "ChatGPT", tag: "OpenAI · LLM partner", domain: "openai.com" },
  { name: "Apple Pay", tag: "Wallet", slug: "applepay" },
  { name: "Perplexity", tag: "AI search", slug: "perplexity" },
  { name: "Klarna", tag: "BNPL", slug: "klarna" },
  { name: "Sift", tag: "Fraud platform", domain: "sift.com" },
  { name: "Cash App", tag: "P2P wallet", slug: "cashapp" },
  { name: "Gemini", tag: "Google · LLM partner", slug: "googlegemini" },
  { name: "Mastercard", tag: "Card scheme", slug: "mastercard" },
  { name: "Signifyd", tag: "Fraud platform", domain: "signifyd.com" },
  { name: "Checkout.com", tag: "Processor", domain: "checkout.com" },
];

const COL_B = [
  { name: "PayPal", tag: "Wallet", slug: "paypal" },
  { name: "Claude", tag: "Anthropic · LLM partner", slug: "anthropic" },
  { name: "Discover", tag: "Card scheme", slug: "discover" },
  { name: "Cursor", tag: "AI dev tools", slug: "cursor" },
  { name: "Afterpay", tag: "BNPL", slug: "afterpay" },
  { name: "Riskified", tag: "Fraud platform", domain: "riskified.com" },
  { name: "Global Payments", tag: "Acquirer", domain: "globalpayments.com" },
  { name: "Primer", tag: "Orchestrator", domain: "primer.io" },
  { name: "Google Pay", tag: "Wallet", slug: "googlepay" },
  { name: "Chase", tag: "Issuing bank", domain: "chase.com" },
  { name: "Spreedly", tag: "Orchestrator", domain: "spreedly.com" },
  { name: "WorldPay", tag: "Acquirer", domain: "worldpay.com" },
  { name: "Fiserv", tag: "Processor", domain: "fiserv.com" },
];

const COL_C = [
  { name: "Gr4vy", tag: "Orchestrator", domain: "gr4vy.com" },
  { name: "Alipay", tag: "Wallet", slug: "alipay" },
  { name: "Hugging Face", tag: "ML community", asset: "/brand/huggingface.webp" },
  { name: "IXOPAY", tag: "Orchestrator", domain: "ixopay.com" },
  { name: "Kount", tag: "Fraud platform · Equifax", logoDev: "equifax.com" },
  { name: "Emergent", tag: "Build partner", domain: "emergent.sh" },
  { name: "Mistral", tag: "EU LLM partner", slug: "mistralai" },
  { name: "xAI", tag: "Grok · LLM partner", logoDev: "x.ai" },
  { name: "CyberSource", tag: "Gateway", domain: "cybersource.com" },
  { name: "Venmo", tag: "P2P wallet", slug: "venmo" },
  { name: "Shop Pay", tag: "Shopify wallet", slug: "shopify" },
  { name: "NMI", tag: "Gateway", domain: "nmi.com" },
  { name: "American Express", tag: "Card scheme", slug: "americanexpress" },
];

const logoSrc = (item) => {
  if (item.asset) return item.asset;
  if (item.slug) return `https://cdn.simpleicons.org/${item.slug}`;
  if (item.logoDev) return `https://img.logo.dev/${item.logoDev}?token=${LOGO_DEV_TOKEN}&format=png&size=256`;
  return `https://www.google.com/s2/favicons?domain=${item.domain}&sz=128`;
};

const tileKey = (item) =>
  (item.slug || item.logoDev || item.domain || item.asset).replace(/[^a-z0-9]+/gi, "-");

/* --- Tile ----------------------------------------------------------------- */

const LogoTile = ({ item }) => {
  const [failed, setFailed] = useState(false);
  return (
    <div
      className="relative aspect-square rounded-xl bg-white flex items-center justify-center p-2 max-w-[82px] w-full mx-auto logo-tile-3d"
      data-testid={`logo-tile-${tileKey(item)}`}
    >
      {failed ? (
        <span className="text-[#0b1e5a] text-xs font-semibold tracking-tight text-center leading-tight">
          {item.name}
        </span>
      ) : (
        <img
          src={logoSrc(item)}
          alt={item.name}
          loading="lazy"
          className="max-h-[70%] max-w-[78%] object-contain"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
};

/* --- Column --------------------------------------------------------------- */

const LogoColumn = ({ items, durationSec, direction = "up", testId, tilt }) => {
  const doubled = [...items, ...items];
  const animName = direction === "up" ? "logoScrollUp" : "logoScrollDown";
  return (
    <div
      className="relative h-full overflow-hidden logo-col-3d"
      data-testid={testId}
      style={{ transform: tilt }}
    >
      <div
        className="flex flex-col gap-5 absolute inset-x-0 logo-col-track"
        style={{ animation: `${animName} ${durationSec}s linear infinite` }}
      >
        {doubled.map((it, idx) => (
          <LogoTile key={`${it.name}-${idx}`} item={it} />
        ))}
      </div>
    </div>
  );
};

/* --- 3-column scroller (hero visual) -------------------------------------- */

export default function LogoColumns() {
  return (
    <div
      className="relative h-full w-full select-none group/logos"
      data-testid="hero-logo-columns"
    >
      <style>{`
        @keyframes logoScrollUp {
          from { transform: translateY(0); }
          to   { transform: translateY(-50%); }
        }
        @keyframes logoScrollDown {
          from { transform: translateY(-50%); }
          to   { transform: translateY(0); }
        }
        .group\\/logos:hover .logo-col-track {
          animation-play-state: paused;
        }
        /* 3D depth: subtle inward tilt on outer columns, slight forward push on the middle column. */
        .logo-col-3d {
          transform-origin: center center;
          backface-visibility: hidden;
          /* Each column gets its own perspective box so the rotation reads cleanly. */
        }
        /* Floating-glass depth on each tile */
        .logo-tile-3d {
          box-shadow:
            0 1px 0 rgba(255,255,255,0.85) inset,
            0 -1px 0 rgba(11,30,90,0.06) inset,
            0 10px 22px -8px rgba(0,0,0,0.35),
            0 2px 4px -1px rgba(0,0,0,0.18),
            0 0 0 1px rgba(255,255,255,0.08);
          transition: transform 280ms ease, box-shadow 280ms ease;
        }
        .logo-tile-3d:hover, .logo-tile-3d:focus-visible {
          transform: translateY(-2px) scale(1.04);
          box-shadow:
            0 1px 0 rgba(255,255,255,0.95) inset,
            0 -1px 0 rgba(11,30,90,0.05) inset,
            0 16px 32px -10px rgba(0,0,0,0.45),
            0 4px 8px -2px rgba(0,0,0,0.22),
            0 0 0 1px rgba(172,98,234,0.4);
          z-index: 20;
          outline: none;
        }
      `}</style>

      {/* Soft ambient backdrop glow */}
      <div
        className="absolute inset-x-0 inset-y-6 -z-0 pointer-events-none"
        aria-hidden
        style={{
          background:
            "radial-gradient(60% 50% at 50% 50%, rgba(172,98,234,0.22) 0%, rgba(11,30,90,0) 70%)",
          filter: "blur(32px)",
        }}
      />

      <div
        className="grid grid-cols-3 gap-5 h-full relative"
        style={{ perspective: "1400px", transformStyle: "preserve-3d" }}
      >
        <LogoColumn
          items={COL_A}
          durationSec={28}
          direction="up"
          testId="logo-col-a"
          tilt="rotateY(7deg) translateZ(-12px)"
        />
        <LogoColumn
          items={COL_B}
          durationSec={36}
          direction="down"
          testId="logo-col-b"
          tilt="translateZ(24px)"
        />
        <LogoColumn
          items={COL_C}
          durationSec={32}
          direction="up"
          testId="logo-col-c"
          tilt="rotateY(-7deg) translateZ(-12px)"
        />
      </div>
    </div>
  );
}
